import React from 'react';
import { motion } from 'framer-motion';
import { Baby, AlertTriangle, Heart, DollarSign } from 'lucide-react';
import { Product } from '../../../types/Product';
import { MenuSuggestion } from '../../../types/Menu';
import { RecipeSteps } from '../recipe/RecipeSteps';

interface BabyMenuDisplayProps {
  menu: MenuSuggestion;
}

export const BabyMenuDisplay: React.FC<BabyMenuDisplayProps> = ({ menu }) => {
  if (!menu.products?.length) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <AlertTriangle className="mx-auto text-amber-500 mb-4" size={32} />
        <p className="text-gray-600">
          No se encontraron productos adecuados para este rango de edad.
          Por favor, consulta con tu pediatra para recomendaciones específicas.
        </p>
      </div>
    );
  }

  const totalPrice = menu.products.reduce((sum, product: Product) => 
    sum + product.current_price, 0
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg p-6 shadow-lg mt-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Baby className="text-pink-500" size={24} />
          <div>
            <h3 className="text-xl font-bold text-gray-800">{menu.name}</h3>
            <p className="text-gray-600">{menu.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-pink-50 px-4 py-2 rounded-lg">
          <DollarSign className="text-pink-500" size={20} />
          <span className="text-lg font-bold text-pink-600">
            {totalPrice.toFixed(2)}€
          </span>
        </div>
      </div>

      <div className="bg-pink-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Heart className="text-pink-500" size={20} />
          Lista de Productos
        </h4>
        <div className="space-y-3">
          {menu.products.map((product: Product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between bg-white p-4 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <img
                  src={product.thumbnail}
                  alt={product.display_name}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div>
                  <h5 className="font-medium text-gray-800">{product.display_name}</h5>
                  <p className="text-sm text-gray-600">{product.brand}</p>
                  <p className="text-xs text-gray-500">{product.category_name}</p>
                </div>
              </div>
              <span className="font-semibold text-pink-600">
                {product.current_price.toFixed(2)}€
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {menu.recipeSteps && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="text-pink-500" size={20} />
            <h3 className="text-lg font-semibold">Preparación segura para bebés</h3>
          </div>
          <RecipeSteps steps={menu.recipeSteps} useThermomix={false} />
        </div>
      )}

      <div className="mt-6 bg-amber-50 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="text-amber-500 mt-1" size={20} />
          <div className="space-y-2 text-sm text-amber-700">
            <p>
              Recuerda introducir los alimentos nuevos de uno en uno y esperar 3-5 días
              entre cada introducción para detectar posibles alergias o intolerancias.
            </p>
            <p>
              Consulta siempre con tu pediatra antes de introducir nuevos alimentos
              en la dieta de tu bebé.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};